/**
 * Stage 3 (Theresa, Oct 22 2026): what the agent learned for this role's search, as explicit rules.
 * The agent proposes (by itself after 3 new reject reasons or a run that missed its target, or on
 * "Suggest changes"); a recruiter accepts, declines or first challenges a rule for bias. Only the
 * SEARCH changes — never the brief or the screening. Accepted rules can be undone.
 *
 * Folded by default (Sofia, Oct 27 2026): the line says how many suggestions wait, so none is missed.
 */
import { useEffect, useState } from 'react';
import { Button } from '@finn-auto/design-system/atoms/button';
import { showToast } from '@finn-auto/design-system/atoms/toast';
import { cn } from '../../../../shared';
import * as api from '../../services/taOutreachApi';
import type { SearchRulesView } from '../../types/taOutreach';
import { list, ruleText } from './searchRuleText';
import { SearchLog } from './SearchLog';
import type { useRoleSearch } from './useRoleSearch';

const RISK = {
  low: { text: 'Low bias risk', className: 'bg-[#DEF5E6] text-[#1D9247]' },
  possible: { text: 'Possible bias risk', className: 'bg-[#FCEEE2] text-[#B96716]' },
  high: { text: 'High bias risk', className: 'bg-[#FCDFE4] text-[#E71D3F]' },
};

export function SearchRules({ postingId, roleSearch, disabled }: { postingId: string; roleSearch: ReturnType<typeof useRoleSearch>; disabled: boolean }) {
  const [view, setView] = useState<SearchRulesView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  /** The rule whose high-risk acceptance waits for the second click. */
  const [confirming, setConfirming] = useState<string | null>(null);
  const hasSearch = Boolean(roleSearch.search?.boolean);

  async function run(key: string, fn: () => Promise<SearchRulesView>) {
    setBusy(key);
    try {
      const next = await fn();
      setView(next);
      if (next.search) roleSearch.replace(next.search);
      return next;
    } catch (err) {
      const code = (err as { body?: { code?: string } })?.body?.code;
      if (code === 'CONFIRM_BIAS') setConfirming(key.split(':')[1] || null);
      else showToast({ status: 'error', position: 'bottomCenter', description: err instanceof Error ? err.message : 'That did not work.' });
      return null;
    } finally {
      setBusy(null);
    }
  }

  useEffect(() => {
    if (!hasSearch) return;
    let live = true;
    void (async () => {
      try {
        const v = await api.fetchSearchRules(postingId);
        if (!live) return;
        setView(v);
        // Derived, not a step: enough new reject reasons or a weak run → the agent suggests by itself.
        if (v.due) await run('propose', () => api.proposeSearchRules(postingId));
      } catch {
        /* no search yet, or offline: the box stays empty */
      }
    })();
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per role and search
  }, [postingId, hasSearch]);

  if (!hasSearch || !view?.signals || !Array.isArray(view.rules)) return null;
  const proposed = view.rules.filter((r) => r.status === 'proposed');
  const inUse = view.rules.filter((r) => r.status === 'accepted');
  const s = view.signals;
  const summary = proposed.length
    ? `${proposed.length} suggestion${proposed.length === 1 ? '' : 's'} waiting`
    : inUse.length
      ? `${inUse.length} rule${inUse.length === 1 ? '' : 's'} in use`
      : 'No suggestions yet';
  return (
    <details className="group rounded-md border border-solid border-[#E9EAEC] bg-white px-4 py-3 text-[13px]" data-testid="search-rules">
      <summary className="flex cursor-pointer list-none items-center gap-2 [&::-webkit-details-marker]:hidden">
        <span className="text-[#707070] transition-transform group-open:rotate-90" aria-hidden>
          ›
        </span>
        <span className="font-semibold text-[#1A1A1A]">What the agent learned</span>
        <span aria-live="polite" className={cn('text-xs', proposed.length ? 'font-semibold text-[#0072EA]' : 'text-[#707070]')}>
          {summary}
        </span>
      </summary>
      <div className="mt-2 space-y-2">
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" size="sm" disabled={disabled || Boolean(busy)} onClick={() => void run('propose', () => api.proposeSearchRules(postingId))}>
            {busy === 'propose' ? 'Thinking…' : 'Suggest changes'}
          </Button>
        </div>
        <p className="m-0 text-xs text-[#707070]">
          From {s.rejections} reject reason{s.rejections === 1 ? '' : 's'} and the recent runs. Only the search changes — never the brief or who counts as a fit.
          {s.protectedLeftOut > 0 && ` ${s.protectedLeftOut} reason${s.protectedLeftOut === 1 ? ' was' : 's were'} left out: about the person, not the work.`}
        </p>
        {proposed.length === 0 && inUse.length === 0 && <p className="m-0 text-xs text-[#707070]">No suggestions yet.</p>}
        {proposed.map((r) => (
          <div key={r.id} className="space-y-1 rounded-md border border-solid border-[#DBEBFC] bg-[#F3F8FE] px-3 py-2">
            <p className="m-0 font-medium text-[#1A1A1A]">{ruleText(r)}</p>
            <p className="m-0 text-xs text-[#707070]">Why: {r.why}</p>
            {r.flags.map((f) => (
              <p key={f.group} className="m-0 text-xs text-[#B96716]">
                ⚠ {list(f.terms)} can stand in for {f.group}. Challenge it before you accept.
              </p>
            ))}
            {r.challenge && (
              <p className="m-0 text-xs">
                <span className={`mr-1 rounded px-1.5 py-0.5 ${RISK[r.challenge.risk].className}`}>{RISK[r.challenge.risk].text}</span>
                {r.challenge.groups.length > 0 && `(${r.challenge.groups.join(', ')}) `}
                {r.challenge.why}
                {r.challenge.saferAlternative && <span className="block text-[#707070]">Safer: {r.challenge.saferAlternative}</span>}
              </p>
            )}
            {view.edited && <p className="m-0 text-xs text-[#707070]">Accepting replaces your own edited search with the brief’s search plus the rules.</p>}
            <div className="flex flex-wrap gap-2 pt-1">
              {confirming === r.id ? (
                <Button variant="primary" size="sm" disabled={disabled || Boolean(busy)} onClick={() => void run(`accept:${r.id}`, () => api.decideSearchRule(postingId, r.id, 'accept', true)).then(() => setConfirming(null))}>
                  Accept anyway
                </Button>
              ) : (
                <Button variant="primary" size="sm" disabled={disabled || Boolean(busy)} onClick={() => void run(`accept:${r.id}`, () => api.decideSearchRule(postingId, r.id, 'accept'))}>
                  Accept
                </Button>
              )}
              <Button variant="outline" size="sm" disabled={disabled || Boolean(busy)} onClick={() => void run(`decline:${r.id}`, () => api.decideSearchRule(postingId, r.id, 'decline'))}>
                Decline
              </Button>
              {!r.challenge && (
                <Button variant="ghost" size="sm" disabled={disabled || Boolean(busy)} onClick={() => void run(`challenge:${r.id}`, () => api.challengeSearchRule(postingId, r.id))}>
                  {busy === `challenge:${r.id}` ? 'Checking…' : 'Challenge for bias'}
                </Button>
              )}
            </div>
          </div>
        ))}
        {inUse.length > 0 && (
          <div>
            <span className="block text-xs font-semibold text-[#707070]">Rules in use</span>
            <ul className="m-0 list-none space-y-1 p-0">
              {inUse.map((r) => (
                <li key={r.id} className="flex flex-wrap items-center justify-between gap-2 text-xs">
                  <span>
                    {ruleText(r)}
                    {r.acceptedDespiteRisk && <span className="ml-1 text-[#E71D3F]">(accepted despite high bias risk)</span>}
                  </span>
                  <Button variant="ghost" size="sm" disabled={disabled || Boolean(busy)} onClick={() => void run(`withdraw:${r.id}`, () => api.decideSearchRule(postingId, r.id, 'withdraw'))}>
                    Undo
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}
        <SearchLog entries={view.log || []} />
      </div>
    </details>
  );
}
